import { Radio } from "@mui/material";
import { CoopInfo } from "@/interfaces";
import CoopInfoItem from "../CoopInfoItem";
import * as S from "./styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import { useState, useEffect, useRef } from "react";
import { colors } from "@/theme";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDealer, useCart, useSettings } from "@/contexts";
import { useDataApi } from "@/hooks";
import axios from "axios";
import { formatCurrency } from "@/utils";
import { OrderWithDetails } from "./interface";

export interface CoopInfoRadioItem extends CoopInfo {
  checked?: boolean;
}

export type ChannelsRadioGroupProps = {
  items?: CoopInfoRadioItem[];
  onChange?: (newValue: string) => void;
  onCardClick?: (channel: string) => void;
};

const ChannelsRadioGroup: React.FC<ChannelsRadioGroupProps> = ({
  items = undefined,
  onChange = undefined,
  onCardClick = undefined,
}) => {
  const [selectedCard, setSelectedCard] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingChannel, setPendingChannel] = useState<string | null>(null);
  const { setCoopInfo, coopInfo, dealer } = useDealer();
  const { setCart } = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [firstOrder, setFirstOrder] = useState<any>(null);
  const firstCardRef = useRef<HTMLButtonElement>(null);
  const { settings } = useSettings();
  useEffect(() => {
    // Auto-focus the first card when component mounts
    if (firstCardRef.current) {
      firstCardRef.current.focus();
    }
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLButtonElement>, channel: string) => {
    if (e.key === 'Enter') {
      handleCardClick(channel);
    }
  };

  if (!Array.isArray(items) || !items.length) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value);
  };

  const checkDealerOrders = async () => {
    if (!dealer?.dealerId) return null;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_REST_BASE_URL}/Orders/withdealer?search=${dealer.dealerId}&sortingCol=dateTimeCreated&sortType=desc`
      );

      const orders = response.data.data;
      if (!orders || !Array.isArray(orders)) {
        console.log('No orders found or invalid response format');
        return null;
      }

      // Find order with status 1 and offsite false
      const orderWithStatusOne = orders.find((order: any) =>
        order.status === 1
      );

      if (orderWithStatusOne) {
        console.log('Found order with status 1:', orderWithStatusOne);
        setFirstOrder(orderWithStatusOne);
        return orderWithStatusOne;
      }

      return null;
    } catch (error) {
      console.error('Error fetching dealer orders:', error);
      return null;
    }
  };

  const handleCardClick = async (channel: string) => {
    console.log("------------------handleCardClick---------------")
    const selectedItem = items.find((item) => item.channel === channel);
    if (selectedItem) {
      setSelectedCard(items.findIndex((item) => item.channel === channel));
      setCoopInfo(selectedItem);

      // Check for existing orders before proceeding
      const existingOrder = await checkDealerOrders();
      if (existingOrder) {
        setDialogOpen(true);
        setPendingChannel(channel);
      } else {
        onCardClick?.("/");
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPendingChannel(null);
    setFirstOrder(null);
  };

  const handleDialogAction = async (action: "continue" | "new") => {
    if (!firstOrder?.orderId) {
      console.error("No order ID found");
      return;
    }

    try {
      if (action === "continue") {
        // Get full order details
        const response = await axios.get<OrderWithDetails>(
          `${process.env.REACT_APP_REST_BASE_URL}/Orders/getorderfull/${firstOrder.orderId}`
        );
        console.log("Full order details:", response.data);
        // Set the cart with the order details
        setCart?.({
          ...response.data,
          shippingFee: settings?.shippingFee,
          coopEligible: settings?.coopEligible,
        });

        // Navigate based on offsite property
        console.log("Order offsite status:", firstOrder.offsite);
        if (firstOrder.offsite === true) {
          console.log("Navigating to offsite route");
          onCardClick?.("/offsite");
        } else {
          console.log("Navigating to home route");
          onCardClick?.("/");
        }
      } else {
        // Cancel the order
        await axios.delete(
          `${process.env.REACT_APP_REST_BASE_URL}/Orders/cancel/${firstOrder.orderId}`
        );
        console.log("Order cancelled successfully");
        // Clear the cart for new order
        setCart?.(undefined);
        // Navigate to home for new order
        onCardClick?.("/");
      }

      // Close dialog
      setDialogOpen(false);
      setPendingChannel(null);
      setFirstOrder(null);
    } catch (error) {
      console.error("Error handling order action:", error);
      // Still close dialog and navigate even if there's an error
      setDialogOpen(false);
      setPendingChannel(null);
      setFirstOrder(null);
    }
  };

  return (
    <S.ChannelsRadioGroupContainer>
      <Card
        sx={{
          border: "none",
          boxShadow: "none",
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          gap: "24px",
          marginTop: "20px",
        }}
      >
        {items.map((item: CoopInfoRadioItem, index: number) => (
          <CardActionArea
            key={item.channel}
            ref={index === 0 ? firstCardRef : null}
            component="button"
            onClick={() => !isLoading && handleCardClick(item.channel || "")}
            onKeyPress={(e) => handleKeyPress(e, item.channel || "")}
            tabIndex={0}
            data-active={selectedCard === index ? "" : undefined}
            sx={{
              width: "auto",
              height: "100%",
              backgroundColor: colors.primaryOzone500,
              opacity: isLoading ? 0.7 : 1,
              pointerEvents: isLoading ? "none" : "auto",
              "&:focus": {
                outline: `2px solid ${colors.white}`,
                outlineOffset: "2px",
              },
              border: "none",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ height: "100%" }}>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "22px",
                  fontWeight: "500",
                  color: colors.white,
                }}
                component="div"
              >
                {item.channel}
              </Typography>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: "400",
                  color: colors.white,
                }}
                variant="body2"
              >
                {formatCurrency(item?.available || 0)} Co-Op Available
              </Typography>
            </CardContent>
          </CardActionArea>
        ))}
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, textAlign: "center" }}>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon sx={{ color: colors.primaryOzone500, fontSize: 35 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pt: 0 }}>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 400,
              mt: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            This customer has a saved session.
          </Typography>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 400,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Would you like to open the saved order or start a new order.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            sx={{
              background: "#2D3748",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              width: "180px",
              fontFamily: "PF DIN Text Pro",
              padding: "16px",
              fontWeight: 500,
              letterSpacing: "0.05em",
              fontSize: "20px",
              "&:hover": { background: "#1A202C" },
            }}
            onClick={() => handleDialogAction("continue")}
          >
            Continue Saved
          </Button>
          <Button
            variant="contained"
            sx={{
              background: "#0066FF",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              width: "180px",
              fontFamily: "PF DIN Text Pro",
              padding: "16px",
              fontWeight: 500,
              letterSpacing: "0.05em",
              fontSize: "20px",
              "&:hover": { background: "#0052CC" },
            }}
            onClick={() => handleDialogAction("new")}
          >
            New Order
          </Button>
        </DialogActions>
      </Dialog>
    </S.ChannelsRadioGroupContainer>
  );
};

export default ChannelsRadioGroup;
